import { useEffect, useRef, useState } from "react";

const WF_SRC = "https://d3pw37i36t41cq.cloudfront.net/embed_v2.js";
const WIDGET_ID = "noCv6xz8Jn3wj9qcs2VFuCH3";
const MAIN_ID = `wf_main_${WIDGET_ID}`;

const INLINE_CSS = `
#${MAIN_ID} { height: auto !important; width: 100% !important; }
#${MAIN_ID} .wf_floater_wrapper,
#${MAIN_ID} .wf_layout.wf_floater {
  position: static !important;
  inset: auto !important;
  top: auto !important;
  bottom: auto !important;
  left: auto !important;
  right: auto !important;
  transform: none !important;
  z-index: auto !important;
  width: 100% !important;
  max-width: 100% !important;
  height: auto !important;
  min-height: 0 !important;
  overflow: visible !important;
  display: block !important;
  pointer-events: auto !important;
}
#${MAIN_ID} .wf_floater_box {
  position: static !important;
  width: 100% !important;
  max-width: 100% !important;
  margin: 0 !important;
  transform: none !important;
}
#${MAIN_ID} [class*="close"],
#${MAIN_ID} [class*="bubble"],
#${MAIN_ID} [class*="minimize"],
#${MAIN_ID} .wf_floater_handler,
#${MAIN_ID} .wf_handler_icon,
#${MAIN_ID} [class*="handler"] {
  display: none !important;
}
`;

function injectInlineCss() {
  if (document.getElementById("wf-inline-overrides")) return;
  const style = document.createElement("style");
  style.id = "wf-inline-overrides";
  style.textContent = INLINE_CSS;
  document.head.appendChild(style);
}

function loadWebinarFuel() {
  if (window.__wfScriptPromise) return window.__wfScriptPromise;
  window._wf = window._wf || [];
  window.__wfScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${WF_SRC}"]`);
    if (existing) {
      if (existing.dataset.loaded === "true") return resolve();
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener("error", () => reject(new Error("wf")), { once: true });
      return;
    }
    const s = document.createElement("script");
    s.src = WF_SRC;
    s.async = true;
    s.onload = () => {
      s.dataset.loaded = "true";
      resolve();
    };
    s.onerror = () => reject(new Error("wf"));
    document.head.appendChild(s);
  });
  return window.__wfScriptPromise;
}

export const WebinarFuelWidget = () => {
  const ref = useRef(null);
  const [error, setError] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;
    let poll;

    const mountInto = () => {
      const el = document.getElementById(MAIN_ID);
      if (!el || !ref.current) return false;
      if (!ref.current.contains(el)) ref.current.appendChild(el);
      injectInlineCss();
      setReady(true);
      return true;
    };

    loadWebinarFuel()
      .then(() => {
        if (!active) return;
        if (!window.__wfPushed) {
          window._wf = window._wf || [];
          window._wf.push({ id: WIDGET_ID });
          window.__wfPushed = true;
        }
        if (mountInto()) return;
        let tries = 0;
        poll = setInterval(() => {
          tries += 1;
          if (mountInto()) {
            clearInterval(poll);
          } else if (tries > 50) {
            clearInterval(poll);
            if (active) setError(true);
          }
        }, 300);
      })
      .catch(() => {
        if (active) setError(true);
      });

    return () => {
      active = false;
      if (poll) clearInterval(poll);
      const el = document.getElementById(MAIN_ID);
      if (el && ref.current && ref.current.contains(el)) {
        document.body.appendChild(el);
      }
    };
  }, []);

  if (error) {
    return (
      <p data-testid="webinarfuel-error" role="alert" className="text-sm text-funred text-center py-8">
        Registration is temporarily unavailable. Please refresh and try again.
      </p>
    );
  }

  return (
    <div>
      {!ready && (
        <p data-testid="webinarfuel-loading" className="text-sm text-slate-400 text-center py-10 animate-pulse">
          Loading registration…
        </p>
      )}
      <div
        ref={ref}
        data-testid="webinarfuel-widget"
        className={`wf-inline-host wf_target wf_target_${WIDGET_ID}`}
        aria-label="Workshop registration"
      />
    </div>
  );
};
